'use client';

import React, { PropsWithChildren } from 'react';
import * as Content from '@/components/content';
import { useLocale } from 'next-intl';
import CapsuleButton from '@/components/button';
import { cn } from '@lib/utils';
import { TrackedAnchor } from '@/components/ancher';

const { Card } = Content;

const CardWrapper = ({ children }: PropsWithChildren) => (
  <div className="grid grid-rows-1 gap-5">{children}</div>
);
const WhiteCard = ({ children }: PropsWithChildren) => (
  <Card className="flex flex-col bg-white p-[26px]">{children}</Card>
);

const List = ({ children, className }: PropsWithChildrenAndClassName) => (
  <ul
    className={cn(
      'font-base ml-5 list-disc break-keep font-medium leading-[26px] tracking-[-0.18px]',
      className,
    )}
  >
    {children}
  </ul>
);

const SamplePickupRequest = () => (
  <Content.Section>
    <Content.Title title={'샘플이 준비되었다면 수거 요청을 해주세요.'} />
    <CardWrapper>
      <WhiteCard>
        <div className="flex flex-col items-start justify-between gap-5 md:flex-row md:items-center">
          <div>
            <Card.Description>
              <b className="text-3b-text-primary">
                요청 시 아래 정보를 알려주세요.
              </b>
              <List className="text-3b-text-primary">
                <li>보내시는 분의 성함, 전화번호</li>
                <li>보내시는 샘플 개수 </li>
                <li>샘플 수거를 원하는 날짜, 시간, 장소</li>
              </List>
            </Card.Description>
          </div>
          <div className="flex gap-4">
            <CapsuleButton size="large" variant="secondary" asChild>
              <TrackedAnchor
                href="https://portal.3billion.io/shipping/sample-pickup"
                label="sample-pickup_online-request"
                position="main_content"
                target="_blank"
                rel="noopener"
              >
                온라인 수거 요청
              </TrackedAnchor>
            </CapsuleButton>
            <CapsuleButton size="large" asChild>
              <TrackedAnchor
                href="tel:02-6258-3770"
                label="sample-pickup_phone-request"
                position="main_content"
              >
                전화로 수거 요청
              </TrackedAnchor>
            </CapsuleButton>
          </div>
        </div>
      </WhiteCard>
    </CardWrapper>
  </Content.Section>
);

const ShipmentRequest = () => (
  <Content.Section>
    <Content.Title title={'착불로 직접 보내주셔도 됩니다.'} />
    <CardWrapper>
      <WhiteCard>
        <div className="flex flex-col items-start justify-between gap-5 md:flex-row md:items-center">
          <div>
            <Card.Description>
              <b className="text-3b-text-primary">
                Buccal swab과 DBS Card만 가능합니다.
              </b>
              <List className="text-3b-text-primary">
                <li>
                  배송지: 서울특별시 강남구 테헤란로 415 8층 쓰리빌리언
                  (우편번호 06160)
                </li>
                <li>
                  샘플 수령 담당자: 쓰리빌리언 샘플 접수 팀 (Tel. 02-6258-3770)
                </li>
              </List>
            </Card.Description>
          </div>
          <CapsuleButton size="large" asChild>
            <TrackedAnchor
              href="https://www.epost.go.kr/"
              target="_blank"
              label="shipment-request_post-office-request"
              position="main_content"
              className="min-w-[170px]"
              rel="noopener noreferrer"
            >
              우체국 택배 신청
            </TrackedAnchor>
          </CapsuleButton>
        </div>
      </WhiteCard>
    </CardWrapper>
  </Content.Section>
);

const ShipmentRequestAndSamplePickUp = () => (
  <Content.Section>
    <Content.Title title={'Shipment Request & Sample Pick up'} />
    <CardWrapper>
      <WhiteCard>
        <div className="flex flex-col items-start justify-between gap-5 md:flex-row md:items-center">
          <div>
            <Card.Title className="pb-4">
              <b>{'How to Request a Sample  Pick-up'}</b>
              <br />
              You can request a sample pick-up through our Portal.
            </Card.Title>
            <Card.Description>
              <List>
                <>
                  <li>
                    Go to <b>My Tests</b> and select{' '}
                    <b>Request a sample pick-up</b>.
                  </li>
                  <li>
                    <b>Need Assistance?</b> If you experience any difficulties
                    requesting a pick-up through the portal, please contact us
                    via email at{' '}
                    <TrackedAnchor
                      href="mailto:support@3billion.io"
                      label="shipment-request_portal-support-email"
                      position="main_content"
                      className="underline"
                    >
                      support@3billion.io
                    </TrackedAnchor>{' '}
                    or WhatsApp at <b>+82 10 2724 5159</b>.
                  </li>
                </>
              </List>
            </Card.Description>
          </div>
          <CapsuleButton size="large" asChild>
            <TrackedAnchor
              label="shipment-request_portal-request"
              position="main_content"
              href="https://portal.3billion.io/shipping/kit-request/form"
              target="_blank"
              rel="noopener"
              className="whitespace-nowrap"
            >
              Request Sample Pick-up
            </TrackedAnchor>
          </CapsuleButton>
        </div>
      </WhiteCard>
      <WhiteCard>
        <Card.Title className="pb-4">
          <b>Shipment pick-up</b>
          <br />
          You will receive a call from our local courier, confirming pick-up.
        </Card.Title>
        <Card.Description>
          <List>
            <>
              <li>
                Please ensure the shipment is ready with all the samples and
                paperwork enclosed.
              </li>
              <li>
                The shipment should arrive at 3billion within 5 business days of
                the pick-up date.
              </li>
            </>
          </List>
        </Card.Description>
      </WhiteCard>
    </CardWrapper>
  </Content.Section>
);

function SamplePickUp() {
  const locale = useLocale();
  const isKorea = locale === 'ko';

  if (isKorea)
    return (
      <>
        <SamplePickupRequest />
        <ShipmentRequest />
      </>
    );
  return <ShipmentRequestAndSamplePickUp />;
}

export default SamplePickUp;
